import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { usePagedGeneratedReportsByReportDocumentId } from "../data/Reports/usePagedGeneratedReportsByReportDocumentId";
import { useContext, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import RefreshLoadingButton from "../shared/RefreshLoadingButton";
import { useTranslation } from "react-i18next";
import { PagingConstants } from "../shared/constants/PagingConstants";
import { DateTemplate } from "../shared/Grid/BodyTemplates";
import { GeneratedReport } from "../shared/interfaces/GeneratedReport";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { DateFilter } from "../shared/DateFilter";
import { Alert, AlertColor, Box, Button, Stack } from "@mui/material";
import DeleteConfirmationButton from "../shared/FormLayout/Buttons/DeleteConfirmationButton";
import { Snackbar } from "../shared/Snackbar";
import RouteBreadcrumbs from "../shared/RouteBreadcrumbs";
import { useReportDocumentById } from "../data/Reports/useReportDocumentById";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useGeneratedReportDelete } from "../data/Reports/useGeneratedReportDelete";
import { AuthContext } from "../contexts/AuthContext";
import { useDownloadPdf } from "./useDownloadPdf";

interface Props {
  reportDocumentId: number;
  customerId: number;
  companyId: number;
  siteId: number;
  lazyState: DataTablePFSEvent;
  onSort: (event: DataTablePFSEvent) => void;
  onFilter: (event: DataTablePFSEvent) => void;
  onPage: (event: DataTablePFSEvent) => void;
}

export function ReportVersionsGrid(props: Props) {
  const { t } = useTranslation();

  const { reportDocumentId, customerId, companyId, siteId, lazyState, onSort, onFilter, onPage } = props;

  const topOfTableAnchorRef = useRef<HTMLDivElement>(null);

  const reportDocument = useReportDocumentById(reportDocumentId);
  const generatedReports = usePagedGeneratedReportsByReportDocumentId(
    lazyState,
    reportDocumentId,
    customerId,
    companyId,
    siteId,
  );
  const authContext = useContext(AuthContext);

  function isTopOfTableInViewport(offset = 0) {
    if (!topOfTableAnchorRef?.current) return false;
    const top = topOfTableAnchorRef?.current.getBoundingClientRect().top;
    return top + offset >= 0 && top - offset <= window.innerHeight;
  }

  function handlePageChange(event: DataTablePFSEvent) {
    //scroll to the top of the table if the top isn't already visible (i.e. when using bottom paginator)
    if (!isTopOfTableInViewport()) topOfTableAnchorRef?.current?.scrollIntoView({ behavior: "smooth" });

    onPage(event);
  }

  const dateFilter = (options: ColumnFilterElementTemplateOptions) => (
    <DateFilter
      showButtonBar={true}
      options={options}
      placeholder={t("common.filter", { val: "$t(reports.createdDate )" })}
    />
  );

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

  const handleErrorSnackbarClose = () => {
    setShowSnackbar(false);
  };

  const generatedReportDelete = useGeneratedReportDelete({
    onSuccess: onDeleteGeneratedReportSuccess,
    onError: onDeleteGeneratedReportError,
  });

  function handleConfirmDeleteGeneratedReportClick(generatedReportId: number) {
    generatedReportDelete.mutate(generatedReportId);
  }

  function onDeleteGeneratedReportSuccess() {
    setSnackbarText(t("reports.versionDeleted"));
    setSnackbarSeverity("success");
    setShowSnackbar(true);
    generatedReports.refetch();
  }

  function onDeleteGeneratedReportError(errorResponse: Response) {
    if (errorResponse.status === 403) {
      setSnackbarText(
        t("common.operationForbiddenError", {
          operation: t("common.delete"),
          item: t("reports.version"),
        }),
      );
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    } else {
      setSnackbarText(t("common.dataDeleteError", { val: "$t(reports.version)" }));
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  }

  const headerToolbar = (
    <Row>
      <Col className="d-flex justify-content-end">
        <RefreshLoadingButton isLoading={generatedReports.isFetching} onClick={generatedReports.refetch} />
      </Col>
    </Row>
  );

  const downloadPdf = useDownloadPdf({
    onSuccess: (url: string) => {
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.click();
    },
    onError: (error: Response) => {
      setSnackbarText(t("reports.pdfDownloadError", { val: "$t(reports.version)" }));
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    },
  });

  const handleDownloadPdfClick = (rowData: GeneratedReport) => {
    downloadPdf.mutate({
      generatedReportId: rowData.id,
      customerId: customerId,
      companyId: companyId,
      siteId: siteId,
    });
  };

  const actionBodyTemplate = (rowData: GeneratedReport) => {
    return (
      <Stack direction="row" spacing={1} justifyContent="center">
        {authContext.canI("view", "reports", customerId) && (
          <Button
            variant="contained"
            color="primary"
            aria-label={t("common.view")}
            onClick={() => handleDownloadPdfClick(rowData)}
          >
            <VisibilityIcon />
          </Button>
        )}
        {authContext.canI("delete", "reports", customerId) && (
          <DeleteConfirmationButton
            confirmationDialogHeader={t("reports.deleteVersionConfirmation", {
              val: rowData.version,
            })}
            confirmationDialogMessage={t("reports.deleteVersionConfirmationShort", { val: rowData.version })}
            onConfirmDeleteButtonClick={() => handleConfirmDeleteGeneratedReportClick(rowData.id)}
          />
        )}
      </Stack>
    );
  };

  return (
    <>
      <Box marginBottom={2}>
        <RouteBreadcrumbs
          customPageTitle={
            generatedReports.isLoading
              ? t("reports.reportVersions")
              : `${t("reports.reportVersions")} - ${reportDocument.data?.name || ""}`
          }
        />
      </Box>
      <div className="datatable-responsive" ref={topOfTableAnchorRef}>
        <DataTable
          value={generatedReports.data?.result}
          header={headerToolbar}
          filters={lazyState.filters}
          filterDisplay="row"
          onFilter={onFilter}
          removableSort
          sortMode="multiple"
          className="p-datatable-responsive p-datatable-gridlines p-datatable-sm"
          emptyMessage={t("common.emptyMessage", {
            val: "$t(results.results )",
          })}
          loading={generatedReports.isLoading}
          sortField={lazyState.sortField}
          sortOrder={lazyState.sortOrder}
          multiSortMeta={lazyState.multiSortMeta}
          onSort={onSort}
          // Paging functionality
          paginator={true}
          rowsPerPageOptions={[
            PagingConstants.TEN_ROW_AMOUNT,
            PagingConstants.INITIAL_ROW_AMOUNT,
            PagingConstants.FIFTY_ROW_AMOUNT,
            PagingConstants.HUNDRED_ROW_AMOUNT,
          ]}
          paginatorPosition="both"
          onPage={handlePageChange}
          first={lazyState.first}
          rows={lazyState.rows}
          totalRecords={generatedReports.data?.totalCount || 0}
          paginatorTemplate={
            "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          }
          currentPageReportTemplate={t("common.firstToLastRecords", {
            first: "{first}",
            last: "{last}",
            totalRecords: "{totalRecords}",
          })}
          lazy={true}
        >
          <Column
            header={t("reports.version")}
            field="version"
            filter
            filterPlaceholder="Version Filter"
            showFilterMenu={false}
            showClearButton={false}
            sortable
            sortField="version"
            style={{ width: "45%" }}
          />
          <Column
            field={"created"}
            header={t("reports.createdDate")}
            body={(rowData: GeneratedReport) => (
              <DateTemplate rowData={rowData} field={"created"} header={t("reports.createdDate")} />
            )}
            filter
            dataType="date"
            filterElement={dateFilter}
            showFilterMenu={false}
            showClearButton={false}
            sortable
            sortField="created"
            style={{ width: "45%" }}
          />
          <Column
            body={actionBodyTemplate}
            headerStyle={{ width: "10%", textAlign: "center" }}
            bodyStyle={{ textAlign: "center", overflow: "visible" }}
            header={t("common.actions")}
          />
        </DataTable>
      </div>
      <Snackbar open={showSnackbar} onClose={handleErrorSnackbarClose}>
        <Alert severity={snackbarSeverity} variant="filled">
          {snackbarText}
        </Alert>
      </Snackbar>
    </>
  );
}
